/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { map, filter } from 'rxjs/operators';
import { AngularFireAuth } from '@angular/fire/compat/auth';

import { AppState } from './services/app-state.service';

import { ReloadMonitoring } from 'src/app/services/background-scripts/reload-monitoring.service';
import { RunOnUserLoadedHook } from 'src/app/services/background-scripts/run-on-user-loaded-hook.service';
import { AuthLostMonitor } from 'src/app/services/background-scripts/auth-lost-monitor.service';

import { fbReporting } from 'src/app/plain-javascript/background-scripts/fb-reporting';
import { fbdbTime } from 'src/app/plain-javascript/background-scripts/fbdb-time';
import { analyticsService } from 'src/app/plain-javascript/services/analytics/AnalyticsService';
import { AnalyticsStateService } from './services/analytics-state.service';
import { RouteUtils } from './services/route-utils.service';

import 'src/app/plain-javascript/services/key-modifier';

import { AngularExporter } from './services/angular-exporter.service';
import { FeatureFlagManager } from './services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { FancyFirebaseState } from './common/fancy-firebase/services/fancy-firebase-state.service';
import { GlobalPresenceMonitor } from './services/background-scripts/global-presence-monitor';
import { frontendInitFancyFirebase } from './plain-javascript/services/frontendInitFancyFirebase';
import { InProgressMigrationCheck } from './common/auth/services/in-progress-migration-check.service';
import { AuthService } from './services/auth.service';
import { ForcedSsoReauthService } from './services/background-scripts/forced-sso-reauth.service';
import { fancyDatabase } from '@touchplan/client-firebase';
import { MatIconRegistry } from '@angular/material/icon';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
	constructor(
		private router: Router,
		private appState: AppState,
		private reloadMonitoring: ReloadMonitoring,
		private runOnUserLoadedHook: RunOnUserLoadedHook,
		private authLostMonitor: AuthLostMonitor,
		private afAuth: AngularFireAuth,
		private angularExporter: AngularExporter,
		private featureFlagManager: FeatureFlagManager,
		private analyticsState: AnalyticsStateService,
		private fancyFirebaseState: FancyFirebaseState,
		private routeUtils: RouteUtils,
		private globalPresenceMonitor: GlobalPresenceMonitor,
		private inProgressMigrationCheck: InProgressMigrationCheck,
		private authService: AuthService,
		private forcedSsoReauthService: ForcedSsoReauthService,
		private matIconRegistry: MatIconRegistry
	) {}

	public ngOnInit() {
		// Register material symbols as the default mat icon font class
		this.matIconRegistry.setDefaultFontSetClass('symbol', 'material-symbols-outlined');
		this.router.initialNavigation();
		frontendInitFancyFirebase(this.fancyFirebaseState, this.featureFlagManager);
		this.featureFlagManager.start(fancyDatabase);
		// Init background stuff
		// First thing that is initialized is FeatureFlagManager
		// After that angularExternals
		// Then fancyFirebaseReferencer should be initialized.
		this.angularExporter.start();

		this.appState.setup();
		fbReporting.addUserIdOb$(this.afAuth.user.pipe(map((user) => (user ? user.uid : null))));
		this.authLostMonitor.start();
		fbdbTime.start();
		this.reloadMonitoring.start();
		analyticsService.start(this.analyticsState, this.routeUtils, this.featureFlagManager);
		this.globalPresenceMonitor.start();

		// We don't cleanup this subscription, because we need it in the entire lifecycle of the app
		this.featureFlagManager
			.getFlag$(DynamicFlagTypes.REFRESH_FOR_NEW_VERSION_ENABLED)
			.pipe(filter((flag) => flag === true))
			.subscribe(() => {
				this.reloadMonitoring.openRefreshNowSnackBarIfVersionsDoesNotMatch();
			});

		this.runOnUserLoadedHook.start();

		this.inProgressMigrationCheck.cleanupAfterMigration();

		this.authService.processRedirectResult();
		this.authService.forceSessionPersistence();
		this.forcedSsoReauthService.start();

		this.globalPresenceMonitor
			.idleLogout$(this.featureFlagManager.getFlag$(DynamicFlagTypes.IDLE_TIMEOUT_ENABLED))
			.subscribe(() => this.authService.logout());
	}
}
